// Términos y privacidad con versión y aceptación probada (0079 · estándar
// Cassimmit puntos 61 a 63). La base decide qué falta aceptar y registra la
// versión, la hora, la IP y el dispositivo; la app solo muestra la casilla.
// SANDBOX: en demo no hay nada pendiente.
import { supabase, hasBackend } from '@/lib/supabase';
import { sessionUser } from '@/lib/sessionUser';

/** Sitio público donde viven las versiones archivadas (/legal/<tipo>-v<n>). */
export const LEGAL_SITE_URL = 'https://vaultex-site.cassimmit.com';

/** Versiones vigentes que muestra el registro. Al publicar una nueva: copia
 *  archivada en public/site/legal/, fila en una migración y este valor
 *  (src/lib/legal.test.ts verifica que los tres coincidan). */
export const CURRENT_LEGAL_VERSIONS = { terms: '1.0', privacy: '1.0' } as const;

export interface LegalDoc {
  kind: 'terms' | 'privacy';
  version: string;
  /** Ruta de la copia archivada, p. ej. /legal/terms-v1.0 */
  url: string;
  effectiveAt: string;
}

export type LegalMethod = 'signup_checkbox' | 'reaccept_checkbox';

function parse(data: unknown): LegalDoc[] {
  if (!Array.isArray(data)) return [];
  return data
    .filter((d): d is Record<string, unknown> => !!d && typeof d === 'object')
    .map((d) => ({
      kind: d.kind === 'privacy' ? 'privacy' : 'terms',
      version: String(d.version ?? ''),
      url: String(d.url ?? ''),
      effectiveAt: String(d.effectiveAt ?? ''),
    }));
}

export const legalService = {
  /** Lo que la persona aún debe aceptar. Lanza si no se pudo comprobar. */
  async pending(): Promise<LegalDoc[]> {
    if (!hasBackend) return [];
    const { data, error } = await supabase.rpc('legal_pending');
    if (error) throw new Error(error.message);
    return parse(data);
  },

  async accept(method: LegalMethod): Promise<boolean> {
    if (!hasBackend) return true;
    const { data, error } = await supabase.rpc('accept_legal', { p_method: method } as never);
    return !error && (data === 'ok' || data === 'nada_pendiente');
  },

  /** Quien marcó la casilla al registrarse (sin sesión todavía, si había que
   *  confirmar el correo) queda registrado en su primer ingreso. Devuelve lo
   *  que sigue pendiente después. */
  async settle(): Promise<LegalDoc[]> {
    const pend = await this.pending();
    if (pend.length === 0 || !hasBackend) return pend;
    const { data } = await sessionUser();
    const meta = data.user?.user_metadata as Record<string, unknown> | undefined;
    if (typeof meta?.legal_signup_at !== 'string') return pend;
    // Solo cuenta para las versiones que vio al registrarse.
    const seen = meta.legal_signup_versions as Record<string, string> | undefined;
    const allSeen = pend.every((d) => seen?.[d.kind] === d.version);
    if (!allSeen) return pend;
    return (await this.accept('signup_checkbox')) ? this.pending() : pend;
  },
};
